import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { useContext } from 'react';
import SessionContext from '../contexts/SessionContext';
import API_URL from '../constants/environment';
import type { BillingCard } from './useGetSettings';

export interface TierInfo {
    /** Upper bound (inclusive) of units for this tier; null means unbounded (the final tier). */
    upTo: number | null;
    /** Per-unit amount charged for units within this tier, smallest currency unit. */
    unitAmount: number | null;
    /** Flat amount charged once for this tier, smallest currency unit. */
    flatAmount: number | null;
}

export interface SubscriptionDetailItem {
    priceId: string;
    productName: string | null;
    quantity: number | null;
    /**
     * Headline per-unit amount for the billing interval, smallest currency unit.
     * Null for tiered prices, where there is no single unit price (see `tiers`).
     */
    unitAmount: number | null;
    billingScheme: string | null;
    /** 'graduated' | 'volume' for tiered prices, otherwise null. */
    tiersMode: string | null;
    tiers: Array<TierInfo> | null;
    currency: string;
    interval: string | null;
    intervalCount: number | null;
    /** Actual amount for this item at its current quantity (tier-aware), smallest currency unit. */
    lineTotal: number | null;
    /** lineTotal normalized to a monthly figure (e.g. annual / 12), smallest currency unit. */
    amountPerMonth: number | null;
}

export interface UpcomingInvoiceLine {
    description: string | null;
    quantity: number | null;
    amount: number;
}

export interface UpcomingInvoiceSummary {
    subtotal: number;
    total: number;
    amountDue: number;
    currency: string;
    /** When the next invoice will be finalized/billed (Unix seconds). */
    nextPaymentAttempt: number | null;
    periodStart: number | null;
    periodEnd: number | null;
    lines: Array<UpcomingInvoiceLine>;
}

export interface SubscriptionDetailSummary {
    id: string;
    status: string;
    /** Unix seconds. */
    currentPeriodStart: number | null;
    currentPeriodEnd: number | null;
    startDate: number | null;
    trialEnd: number | null;
    collectionMethod: string | null;
    currency: string;
    latestInvoiceId: string | null;
    /** True only when Stripe reports the subscription as actually canceled. */
    isCanceled: boolean;
    /** Whether the subscription is scheduled to cancel (at period end or a set date). */
    willCancel: boolean;
    /** Whether it will cancel automatically at the end of the current period. */
    cancelAtPeriodEnd: boolean;
    /** Future date the subscription is scheduled to cancel, if scheduled; else null. */
    cancelAt: number | null;
    /** The date the subscription was actually canceled; null unless it is canceled. */
    canceledAt: number | null;
    /** The date the subscription actually ended; null if still running. */
    endedAt: number | null;
    /** Customer-provided cancellation reason/feedback, if any. */
    cancellationReason: string | null;
    /** Sum of item line totals for the billing interval, smallest currency unit. */
    recurringTotal: number;
    /** Sum of item monthly amounts, smallest currency unit. */
    monthlyTotal: number;
    /** Stripe's authoritative preview of the next invoice. Null if unavailable. */
    upcomingInvoice: UpcomingInvoiceSummary | null;
    items: Array<SubscriptionDetailItem>;
}

export interface PaymentRecord {
    id: string;
    number: string | null;
    status: string | null;
    billingReason: string | null;
    amountDue: number;
    amountPaid: number;
    amountRemaining: number;
    total: number;
    currency: string;
    /** Unix seconds. */
    created: number;
    paidAt: number | null;
    periodStart: number | null;
    periodEnd: number | null;
    hostedInvoiceUrl: string | null;
    invoicePdf: string | null;
    subscriptionId: string | null;
}

export interface SubscriptionResponse {
    customer: {
        id: string;
        email: string | null;
        name: string | null;
        currency: string | null;
        balance: number;
        delinquent: boolean;
        defaultPaymentMethod: BillingCard | null;
    } | null;
    subscriptions: Array<SubscriptionDetailSummary>;
    paymentHistory: Array<PaymentRecord>;
    totals: {
        currency: string | null;
        totalPaid: number;
        totalOutstanding: number;
        invoiceCount: number;
    };
}

const getSubscription = async (
    sessionId: string,
    subscriptionId: string
): Promise<SubscriptionResponse> => {
    const { data } = await axios.get(`${API_URL}/subscription`, {
        params: { subscriptionId },
        headers: { Authorization: sessionId },
    });
    return data;
};

export const useGetSubscription = (subscriptionId: string | null, isOpen: boolean) => {
    const sessionContext = useContext(SessionContext);
    return useQuery({
        queryKey: ['/subscription', subscriptionId],
        queryFn: () => getSubscription(sessionContext?.sessionId ?? '', subscriptionId!),
        enabled: !!subscriptionId && !!sessionContext?.sessionId && isOpen,
    });
};
